import React, { Component } from 'react';

interface ErrorBoundaryProps {
  children: React.ReactNode;
  /** Optional fallback UI — receives the error and a reset function */
  fallback?: (error: Error, reset: () => void) => React.ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

/**
 * React Error Boundary — catches rendering errors in child tree.
 * Displays a user-friendly error screen with a "Try again" button.
 */
export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo): void {
    console.error('[Absence Budget] Uncaught rendering error:', error, info.componentStack);
  }

  private handleReset = (): void => {
    this.setState({ hasError: false, error: null });
  };

  render(): React.ReactNode {
    if (this.state.hasError && this.state.error) {
      if (this.props.fallback) {
        return this.props.fallback(this.state.error, this.handleReset);
      }

      return (
        <div className="error-boundary" role="alert">
          <div className="error-boundary__content">
            <h2 className="error-boundary__title">Something went wrong</h2>
            <p className="error-boundary__message">
              An unexpected error occurred. Your data has not been lost.
            </p>
            <details className="error-boundary__details">
              <summary>Technical details</summary>
              <pre className="error-boundary__stack">
                {this.state.error.message}
              </pre>
            </details>
            <div className="error-boundary__actions">
              <button
                className="btn btn--primary btn--md"
                onClick={this.handleReset}
                type="button"
              >
                Try again
              </button>
              <button
                className="btn btn--secondary btn--md"
                onClick={() => window.location.reload()}
                type="button"
              >
                Reload page
              </button>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
